import React, { useMemo } from 'react';
import { Submission, Participant } from '../types';
import { UsersIcon } from './icons';
import Avatar from './Avatar';

/** Convert Firestore Timestamp, ISO string, or Date to a JS Date */
const toDate = (value: any): Date => {
    if (!value) return new Date(0);
    if (typeof value?.toDate === 'function') return value.toDate();
    if (value instanceof Date) return value;
    return new Date(value);
};

const formatTimeAgo = (timestamp: any): string => {
    const date = toDate(timestamp);
    if (isNaN(date.getTime())) return '—';

    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 0) return 'Just now';

    let interval = seconds / 31536000;
    if (interval > 1) return `${Math.floor(interval)}y ago`;

    interval = seconds / 2592000;
    if (interval > 1) return `${Math.floor(interval)}mo ago`;

    interval = seconds / 86400;
    if (interval > 1) return `${Math.floor(interval)}d ago`;

    interval = seconds / 3600;
    if (interval > 1) return `${Math.floor(interval)}h ago`;

    interval = seconds / 60;
    if (interval > 1) return `${Math.floor(interval)}m ago`;

    return `Just now`;
}

interface ParticipantActivityProps {
    submissions: Submission[];
    participantsMap: Map<string, Participant>;
    onSubmissionSelect: (submission: Submission) => void;
}

const ParticipantActivity: React.FC<ParticipantActivityProps> = ({ submissions, participantsMap, onSubmissionSelect }) => {
    const recentActivity = useMemo(() => {
        const sortedSubmissions = [...submissions].sort((a, b) => toDate(b.timestamp).getTime() - toDate(a.timestamp).getTime());

        const participantLastSubmission = new Map<string, Submission>();
        for (const submission of sortedSubmissions) {
            if (!participantLastSubmission.has(submission.participantId)) {
                participantLastSubmission.set(submission.participantId, submission);
            }
        }

        return Array.from(participantLastSubmission.values())
            .sort((a, b) => toDate(b.timestamp).getTime() - toDate(a.timestamp).getTime())
            .slice(0, 7);
    }, [submissions]);

    return (
        <div className="bg-white rounded-xl border border-app-border shadow-sm p-6 flex flex-col min-h-[300px] h-[450px] resize-y overflow-hidden" style={{ resize: 'vertical' }}>
            <h3 className="text-sm font-medium text-text-ink mb-4 border-b border-app-border pb-3 flex-none">Participant Activity</h3>

            <div className="flex-1 overflow-y-auto pr-2 scrollbar-thumb-neutral-200 scrollbar-track-transparent">
                {recentActivity.length > 0 ? (
                    <div className="space-y-1">
                        {recentActivity.map(submission => {
                            const participant = participantsMap.get(submission.participantId);
                            if (!participant) return null;
                            return (
                                <button
                                    key={submission.id}
                                    onClick={() => onSubmissionSelect(submission)}
                                    className="w-full text-left flex items-center space-x-3 p-2 rounded-xl hover:bg-app-bg transition-colors focus:outline-none"
                                >
                                    <Avatar name={participant.name} avatarUrl={participant.avatarUrl} id={participant.id} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                                    <div className="flex-1 overflow-hidden">
                                        <p className="text-[13px] font-bold text-text-ink truncate">{participant.name}</p>
                                        <p className="text-[11px] font-medium text-text-muted opacity-80 uppercase tracking-wider">Last submission: {formatTimeAgo(submission.timestamp)}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center text-center text-text-muted py-8">
                        <UsersIcon className="w-10 h-10 text-text-faint mb-2" />
                        <p className="text-sm">No participant activity yet.</p>
                        <p className="text-xs">Submissions will appear here as they come in.</p>
                    </div>
                )}
            </div>

            {/* Drag Handle */}
            <div className="flex-none flex justify-center pt-3 pb-1 cursor-ns-resize opacity-30 hover:opacity-60 transition-opacity">
                <div className="w-10 h-1 bg-neutral-300 rounded-full"></div>
            </div>
        </div>
    );
};

export default ParticipantActivity;